import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productsData } from '../../mocks/products';
import { priceDropNotifications } from '../../mocks/priceAlerts';
import { emailTemplates, emailCategories } from '../../mocks/emailTemplates';
import { activityLogs, actionTypes } from '../../mocks/activityLogs';

type TabType = 'dashboard' | 'analytics' | 'products' | 'users' | 'alerts' | 'emails' | 'activity' | 'app-settings' | 'settings';

const mockUsers = [
  { id: 1, name: 'John Doe', email: 'john@example.com', status: 'active', alerts: 5, joined: '2024-01-15' },
  { id: 2, name: 'Sarah Smith', email: 'sarah@example.com', status: 'active', alerts: 12, joined: '2024-02-20' },
  { id: 3, name: 'Mike Johnson', email: 'mike@example.com', status: 'inactive', alerts: 3, joined: '2024-03-10' },
  { id: 4, name: 'Emily Brown', email: 'emily@example.com', status: 'active', alerts: 8, joined: '2024-03-25' },
  { id: 5, name: 'David Wilson', email: 'david@example.com', status: 'active', alerts: 15, joined: '2024-04-05' },
];

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [importedCount, setImportedCount] = useState(0);
  
  // Current user role (in real app, this would come from auth context)
  const [currentUserRole, setCurrentUserRole] = useState<'super_admin' | 'admin' | 'editor' | 'viewer'>('super_admin');
  
  // User roles and permissions
  const [rolePermissions, setRolePermissions] = useState({
    super_admin: {
      name: 'Super Admin',
      description: 'Full access to all settings and features',
      color: 'bg-red-100 text-red-700',
      permissions: {
        viewDashboard: true,
        viewAnalytics: true,
        manageProducts: true,
        manageUsers: true,
        manageAlerts: true,
        manageEmails: true,
        viewActivityLogs: true,
        modifyAppSettings: true,
        modifyTheme: true,
        modifyRoles: true,
        backupRestore: true,
        maintenanceMode: true,
      },
    },
    admin: {
      name: 'Admin',
      description: 'Can manage most settings except roles and critical features',
      color: 'bg-amber-100 text-amber-700',
      permissions: {
        viewDashboard: true,
        viewAnalytics: true,
        manageProducts: true,
        manageUsers: true,
        manageAlerts: true,
        manageEmails: true,
        viewActivityLogs: true,
        modifyAppSettings: true,
        modifyTheme: true,
        modifyRoles: false,
        backupRestore: true,
        maintenanceMode: false,
      },
    },
    editor: {
      name: 'Editor',
      description: 'Can manage products and content, limited settings access',
      color: 'bg-teal-100 text-teal-700',
      permissions: {
        viewDashboard: true,
        viewAnalytics: true,
        manageProducts: true,
        manageUsers: false,
        manageAlerts: true,
        manageEmails: false,
        viewActivityLogs: true,
        modifyAppSettings: false,
        modifyTheme: false,
        modifyRoles: false,
        backupRestore: false,
        maintenanceMode: false,
      },
    },
    viewer: {
      name: 'Viewer',
      description: 'Read-only access to dashboard and analytics',
      color: 'bg-gray-100 text-gray-700',
      permissions: {
        viewDashboard: true,
        viewAnalytics: true,
        manageProducts: false,
        manageUsers: false,
        manageAlerts: false,
        manageEmails: false,
        viewActivityLogs: false,
        modifyAppSettings: false,
        modifyTheme: false,
        modifyRoles: false,
        backupRestore: false,
        maintenanceMode: false,
      },
    },
  });

  const permissionLabels: Record<string, { label: string; description: string; icon: string }> = {
    viewDashboard: { label: 'View Dashboard', description: 'Access to main dashboard', icon: 'ri-dashboard-line' },
    viewAnalytics: { label: 'View Analytics', description: 'Access to analytics and reports', icon: 'ri-bar-chart-box-line' },
    manageProducts: { label: 'Manage Products', description: 'Add, edit, delete products', icon: 'ri-shopping-bag-line' },
    manageUsers: { label: 'Manage Users', description: 'View and manage user accounts', icon: 'ri-user-settings-line' },
    manageAlerts: { label: 'Manage Alerts', description: 'Configure price alerts', icon: 'ri-notification-3-line' },
    manageEmails: { label: 'Manage Emails', description: 'Edit email templates', icon: 'ri-mail-settings-line' },
    viewActivityLogs: { label: 'View Activity Logs', description: 'Access to activity history', icon: 'ri-history-line' },
    modifyAppSettings: { label: 'Modify App Settings', description: 'Change site identity and regional settings', icon: 'ri-settings-3-line' },
    modifyTheme: { label: 'Modify Theme', description: 'Change colors, fonts, and appearance', icon: 'ri-palette-line' },
    modifyRoles: { label: 'Modify Roles', description: 'Change user role permissions', icon: 'ri-shield-user-line' },
    backupRestore: { label: 'Backup & Restore', description: 'Create and restore backups', icon: 'ri-database-2-line' },
    maintenanceMode: { label: 'Maintenance Mode', description: 'Enable/disable maintenance mode', icon: 'ri-tools-line' },
  };

  // Check if current user has permission
  const hasPermission = (permission: keyof typeof rolePermissions.super_admin.permissions) => {
    return rolePermissions[currentUserRole]?.permissions[permission] ?? false;
  };

  // Update role permission
  const updateRolePermission = (role: keyof typeof rolePermissions, permission: string, value: boolean) => {
    if (!hasPermission('modifyRoles')) return;
    if (role === 'super_admin') return; // Can't modify super admin permissions
    
    setRolePermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        permissions: {
          ...prev[role].permissions,
          [permission]: value,
        },
      },
    }));
  };

  // Notification dropdown state
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [readNotifications, setReadNotifications] = useState<string[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<{
    id: string;
    type: 'activity' | 'alert';
    title: string;
    message: string;
    time: string;
    icon: string;
    color: string;
    read: boolean;
    details?: any;
  } | null>(null);
  const [showNotificationDetail, setShowNotificationDetail] = useState(false);

  // Bulk editing states
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditField, setBulkEditField] = useState<'category' | 'brand' | 'price' | 'discount'>('category');
  const [bulkEditValue, setBulkEditValue] = useState('');

  // Individual product edit modal
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<typeof productsData[0] | null>(null);

  // Product search in admin
  const [productSearchQuery, setProductSearchQuery] = useState('');

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf'>('csv');
  const [exportType, setExportType] = useState<'products' | 'analytics' | 'alerts'>('analytics');
  const [isExporting, setIsExporting] = useState(false);

  // Email template states
  const [selectedTemplate, setSelectedTemplate] = useState<typeof emailTemplates[0] | null>(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Activity log states
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityDateRange, setActivityDateRange] = useState('7days');
  const [activitySearchQuery, setActivitySearchQuery] = useState('');

  // Price alert threshold settings
  const [alertSettings, setAlertSettings] = useState({
    minThreshold: 5,
    maxThreshold: 50,
    defaultThreshold: 10,
    enableAutoAlerts: true,
    alertFrequency: 'instant',
    maxAlertsPerUser: 20,
    priceDropPercentage: 5,
    enablePriceIncrease: false,
  });

  // Analytics data
  const analyticsData = useMemo(() => {
    const topPerformers = [...productsData]
      .sort((a, b) => (b.priceChange ? Math.abs(b.priceChange) : 0) - (a.priceChange ? Math.abs(a.priceChange) : 0))
      .slice(0, 5);
    
    const categoryStats = productsData.reduce((acc, product) => {
      if (!acc[product.category]) {
        acc[product.category] = { count: 0, totalValue: 0, avgDiscount: 0 };
      }
      acc[product.category].count += 1;
      acc[product.category].totalValue += product.currentPrice || product.lowestPrice;
      acc[product.category].avgDiscount += Math.abs(product.priceChange || 0);
      return acc;
    }, {} as Record<string, { count: number; totalValue: number; avgDiscount: number }>);

    Object.keys(categoryStats).forEach(key => {
      categoryStats[key].avgDiscount = Math.round(categoryStats[key].avgDiscount / categoryStats[key].count);
    });

    const brandStats = productsData.reduce((acc, product) => {
      if (!acc[product.brand]) {
        acc[product.brand] = { count: 0, totalValue: 0 };
      }
      acc[product.brand].count += 1;
      acc[product.brand].totalValue += product.currentPrice || product.lowestPrice;
      return acc;
    }, {} as Record<string, { count: number; totalValue: number }>);

    const totalInventoryValue = productsData.reduce((sum, p) => sum + (p.currentPrice || p.lowestPrice), 0);
    const avgPriceChange = Math.round(productsData.reduce((sum, p) => sum + Math.abs(p.priceChange || 0), 0) / productsData.length);

    return { topPerformers, categoryStats, brandStats, totalInventoryValue, avgPriceChange };
  }, []);

  const stats = {
    totalProducts: productsData.length,
    totalUsers: mockUsers.length,
    activeAlerts: priceDropNotifications.filter(a => a.status === 'active').length,
    triggeredToday: 12,
    totalSavings: 2450,
    avgDiscount: 15,
  };

  // Handle edit product
  const handleEditProduct = (product: typeof productsData[0]) => {
    setEditingProduct({ ...product });
    setShowEditProductModal(true);
  };

  const handleSaveProduct = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingProduct) return;
    
    // In a real app, this would be an API call
    console.log('Saving product:', editingProduct);
    setShowEditProductModal(false);
    setEditingProduct(null);
  };

  // Export functions
  const exportToCSV = (data: any[], filename: string) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => 
      Object.values(item).map(val => 
        typeof val === 'string' && val.includes(',') ? `"${val}"` : val
      ).join(',')
    );
    const csvContent = [headers, ...rows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = (data: any[], title: string) => {
    // Create a printable HTML document
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableHeaders = Object.keys(data[0]);
    const tableRows = data.map(item => 
      `<tr>${Object.values(item).map(val => `<td style="border: 1px solid #ddd; padding: 8px;">${val}</td>`).join('')}</tr>`
    ).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #0d9488; margin-bottom: 20px; }
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th { background-color: #0d9488; color: white; padding: 12px 8px; text-align: left; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <p class="meta">Generated on: ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>${tableHeaders.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExport = () => {
    setIsExporting(true);
    
    setTimeout(() => {
      let data: any[] = [];
      let filename = '';
      let title = '';

      if (exportType === 'products') {
        data = productsData.map(p => ({
          ID: p.id,
          Name: p.name,
          Brand: p.brand,
          Category: p.category,
          'Current Price': `$${p.currentPrice || p.lowestPrice}`,
          'Lowest Price': `$${p.lowestPrice}`,
          'Highest Price': `$${p.highestPrice}`,
          'Price Change': `${p.priceChange}%`,
          Retailers: p.retailers?.length || 0,
        }));
        filename = 'products-report';
        title = 'Products Report';
      } else if (exportType === 'analytics') {
        data = [
          { Metric: 'Total Inventory Value', Value: `$${analyticsData.totalInventoryValue.toLocaleString()}` },
          { Metric: 'Average Price Change', Value: `${analyticsData.avgPriceChange}%` },
          { Metric: 'Total Products', Value: productsData.length },
          { Metric: 'Total Categories', Value: Object.keys(analyticsData.categoryStats).length },
          { Metric: 'Total Brands', Value: Object.keys(analyticsData.brandStats).length },
          ...Object.entries(analyticsData.categoryStats).map(([cat, stats]) => ({
            Metric: `Category: ${cat}`,
            Value: `${stats.count} products, $${stats.totalValue.toLocaleString()} value, ${stats.avgDiscount}% avg discount`,
          })),
        ];
        filename = 'analytics-report';
        title = 'Analytics Report';
      } else if (exportType === 'alerts') {
        data = priceDropNotifications.map(a => ({
          ID: a.id,
          Product: a.productName,
          'Target Price': `$${a.targetPrice}`,
          'Original Price': `$${a.originalPrice}`,
          'Dropped Price': a.droppedPrice ? `$${a.droppedPrice}` : 'N/A',
          Status: a.status,
          'Created At': new Date(a.createdAt).toLocaleDateString(),
        }));
        filename = 'alerts-report';
        title = 'Price Alerts Report';
      }

      if (exportFormat === 'csv') {
        exportToCSV(data, filename);
      } else {
        exportToPDF(data, title);
      }

      setIsExporting(false);
      setShowExportModal(false);
    }, 1000);
  };

  // Handle add product form submission
  const handleAddProduct = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const newProduct = {
      id: productsData.length + 1,
      name: formData.get('name') as string,
      brand: formData.get('brand') as string,
      category: formData.get('category') as string,
      lowestPrice: parseFloat(formData.get('lowestPrice') as string),
      highestPrice: parseFloat(formData.get('highestPrice') as string),
      image: formData.get('image') as string || 'https://readdy.ai/api/search-image?query=product%20placeholder&width=400&height=400&seq=default-product&orientation=squarish',
      retailers: [],
      rating: 0,
      reviews: 0,
      discount: 0,
    };

    console.log('Adding product:', newProduct);
    setShowAddProductModal(false);
    e.currentTarget.reset();
  };

  // Download CSV template
  const downloadCSVTemplate = () => {
    const csvContent = 'name,brand,category,lowestPrice,highestPrice,image\n' +
      'iPhone 15 Pro,Apple,Phones,999.00,1199.00,https://example.com/image.jpg\n' +
      'MacBook Pro M3,Apple,Laptops,1999.00,2499.00,https://example.com/image.jpg';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle CSV file upload
  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      alert('Please upload a valid CSV file');
    }
  };

  // Handle CSV import
  const handleImportCSV = async () => {
    if (!csvFile) return;

    setImportStatus('processing');

    setTimeout(() => {
      try {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target?.result as string;
          const lines = text.split('\n');
          const dataLines = lines.slice(1).filter(line => line.trim());
          
          const products = dataLines.map((line, index) => {
            const [name, brand, category, lowestPrice, highestPrice, image] = line.split(',');
            return {
              id: productsData.length + index + 1,
              name: name?.trim(),
              brand: brand?.trim(),
              category: category?.trim(),
              lowestPrice: parseFloat(lowestPrice?.trim()),
              highestPrice: parseFloat(highestPrice?.trim()),
              image: image?.trim() || 'https://readdy.ai/api/search-image?query=product%20placeholder&width=400&height=400&seq=default-product&orientation=squarish',
              retailers: [],
              rating: 0,
              reviews: 0,
              discount: 0,
            };
          });

          console.log('Importing products:', products);
          setImportedCount(products.length);
          setImportStatus('success');
          
          setTimeout(() => {
            setShowImportModal(false);
            setImportStatus('idle');
            setCsvFile(null);
          }, 2000);
        };
        
        reader.onerror = () => {
          setImportStatus('error');
        };
        
        reader.readAsText(csvFile);
      } catch (error) {
        console.error('Import error:', error);
        setImportStatus('error');
      }
    }, 1500);
  };

  // Bulk edit handlers
  const handleSelectAll = () => {
    if (selectedProducts.length === filteredAdminProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredAdminProducts.map(p => p.id));
    }
  };

  const handleSelectProduct = (id: number) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(pid => pid !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };

  const handleBulkEdit = () => {
    console.log('Bulk editing products:', selectedProducts, 'Field:', bulkEditField, 'Value:', bulkEditValue);
    setShowBulkEditModal(false);
    setSelectedProducts([]);
    setBulkEditValue('');
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
      console.log('Deleting products:', selectedProducts);
      setSelectedProducts([]);
    }
  };

  // Filtered products for admin search
  const filteredAdminProducts = useMemo(() => {
    if (!productSearchQuery) return productsData;
    const query = productSearchQuery.toLowerCase();
    return productsData.filter(
      p =>
        p.name.toLowerCase().includes(query) ||
        p.brand.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query)
    );
  }, [productSearchQuery]);

  // Filtered email templates
  const filteredTemplates = useMemo(() => {
    if (templateFilter === 'all') return emailTemplates;
    return emailTemplates.filter(t => t.category === templateFilter);
  }, [templateFilter]);

  // Filtered activity logs
  const filteredActivityLogs = useMemo(() => {
    let logs = [...activityLogs];
    
    // Filter by action type
    if (activityFilter !== 'all') {
      logs = logs.filter(log => log.action === activityFilter);
    }
    
    // Filter by search query
    if (activitySearchQuery) {
      const query = activitySearchQuery.toLowerCase();
      logs = logs.filter(log =>
        log.userName.toLowerCase().includes(query) ||
        log.userEmail.toLowerCase().includes(query) ||
        log.productName?.toLowerCase().includes(query)
      );
    }
    
    // Filter by date range
    const now = new Date();
    if (activityDateRange !== 'all') {
      logs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        const diffHours = (now.getTime() - logDate.getTime()) / (1000 * 60 * 60);
        
        if (activityDateRange === '24hours') return diffHours <= 24;
        if (activityDateRange === '7days') return diffHours <= 24 * 7;
        if (activityDateRange === '30days') return diffHours <= 24 * 30;
        return true;
      });
    }
    
    return logs;
  }, [activityFilter, activitySearchQuery, activityDateRange]);

  // Activity stats
  const activityStats = useMemo(() => {
    return {
      total: activityLogs.length,
      alertsCreated: activityLogs.filter(log => log.action === 'alert_created').length,
      alertsTriggered: activityLogs.filter(log => log.action === 'alert_triggered').length,
      emailsSent: activityLogs.filter(log => log.action === 'email_sent').length,
    };
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getActionStyle = (action: string) => {
    const actionType = actionTypes.find(a => a.id === action);
    return actionType || { icon: 'ri-question-line', color: 'bg-gray-100 text-gray-600' };
  };

  // Admin notifications derived from activity logs and alerts
  const adminNotifications = useMemo(() => {
    const notifications = [
      ...activityLogs.slice(0, 5).map(log => ({
        id: `activity-${log.id}`,
        type: 'activity' as const,
        title: log.actionLabel,
        message: `${log.userName} - ${log.productName || 'System action'}`,
        time: log.timestamp,
        icon: actionTypes.find(a => a.id === log.action)?.icon || 'ri-notification-3-line',
        color: actionTypes.find(a => a.id === log.action)?.color || 'bg-gray-100 text-gray-600',
        read: readNotifications.includes(`activity-${log.id}`),
        details: {
          userName: log.userName,
          userEmail: log.userEmail,
          productName: log.productName,
          action: log.action,
          actionLabel: log.actionLabel,
          ipAddress: log.ipAddress,
          userAgent: log.userAgent,
          extraDetails: log.details,
        },
      })),
      ...priceDropNotifications.filter(a => a.status === 'triggered').slice(0, 3).map(alert => ({
        id: `alert-${alert.id}`,
        type: 'alert' as const,
        title: 'Price Alert Triggered',
        message: `${alert.productName} dropped to $${alert.droppedPrice}`,
        time: alert.triggeredAt || alert.createdAt,
        icon: 'ri-price-tag-3-line',
        color: 'bg-green-100 text-green-600',
        read: readNotifications.includes(`alert-${alert.id}`),
        details: {
          productName: alert.productName,
          productImage: alert.productImage,
          originalPrice: alert.originalPrice,
          targetPrice: alert.targetPrice,
          droppedPrice: alert.droppedPrice,
          savings: alert.originalPrice - (alert.droppedPrice || alert.originalPrice),
          status: alert.status,
          createdAt: alert.createdAt,
          triggeredAt: alert.triggeredAt,
        },
      })),
    ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 8);
    
    return notifications;
  }, [readNotifications]);

  const unreadCount = adminNotifications.filter(n => !n.read).length;

  // Mark notification as read
  const handleNotificationClick = (notification: typeof adminNotifications[0]) => {
    if (!readNotifications.includes(notification.id)) {
      setReadNotifications([...readNotifications, notification.id]);
    }
    setSelectedNotification(notification);
    setShowNotificationDetail(true);
  };

  // Mark all as read
  const handleMarkAllAsRead = () => {
    const allIds = adminNotifications.map(n => n.id);
    setReadNotifications(allIds);
  };

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatNotificationTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Products</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalProducts}</p>
            </div>
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <i className="ri-shopping-bag-line text-xl text-teal-600"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> 12% from last month</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.totalUsers}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <i className="ri-user-line text-xl text-emerald-600"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> 8% from last month</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Alerts</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.activeAlerts}</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <i className="ri-notification-3-line text-xl text-amber-600"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> 24% from last month</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Savings</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">${stats.totalSavings}</p>
            </div>
            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-rose-600"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> 18% from last month</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Price Alerts Activity</h3>
          <div className="h-64 flex items-end justify-between gap-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
              <div key={day} className="flex-1 flex flex-col items-center gap-2">
                <div 
                  className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg transition-all hover:from-teal-600 hover:to-teal-500"
                  style={{ height: `${[65, 45, 80, 55, 90, 40, 70][i]}%` }}
                />
                <span className="text-xs text-gray-500">{day}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Top Categories</h3>
          <div className="space-y-4">
            {[
              { name: 'Electronics', value: 45, color: 'bg-teal-500' },
              { name: 'Phones', value: 30, color: 'bg-emerald-500' },
              { name: 'Cameras', value: 15, color: 'bg-amber-500' },
              { name: 'Kitchen', value: 10, color: 'bg-rose-500' },
            ].map(cat => (
              <div key={cat.name}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700">{cat.name}</span>
                  <span className="text-gray-500">{cat.value}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${cat.color} rounded-full`} style={{ width: `${cat.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[
            { icon: 'ri-notification-3-line', color: 'text-teal-600 bg-teal-100', text: 'Price alert triggered for iPhone 15 Pro', time: '2 min ago' },
            { icon: 'ri-user-add-line', color: 'text-emerald-600 bg-emerald-100', text: 'New user registered: emily@example.com', time: '15 min ago' },
            { icon: 'ri-price-tag-3-line', color: 'text-amber-600 bg-amber-100', text: 'MacBook Pro M3 price dropped by 10%', time: '1 hour ago' },
            { icon: 'ri-heart-line', color: 'text-rose-600 bg-rose-100', text: 'User shared wishlist with 5 friends', time: '2 hours ago' },
          ].map((activity, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${activity.color}`}>
                <i className={activity.icon}></i>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{activity.text}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowExportModal(true)}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-download-line"></i>
          Export Report
        </button>
      </div>

      {/* Analytics Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Inventory Value</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">${analyticsData.totalInventoryValue.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-xl text-white"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> 8.2% from last month</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Price Change</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{analyticsData.avgPriceChange}%</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <i className="ri-percent-line text-xl text-white"></i>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-3"><i className="ri-arrow-up-line"></i> Better deals available</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Categories</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{Object.keys(analyticsData.categoryStats).length}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center">
              <i className="ri-folder-line text-xl text-white"></i>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Active product categories</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Brands Tracked</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{Object.keys(analyticsData.brandStats).length}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
              <i className="ri-building-line text-xl text-white"></i>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Unique brands in catalog</p>
        </div>
      </div>

      {/* Top Performers Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">Top Performing Products</h3>
              <p className="text-sm text-gray-500 mt-1">Products with the biggest price drops and highest engagement</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">Live Data</span>
            </div>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {analyticsData.topPerformers.map((product, index) => (
            <div key={product.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 text-white font-bold text-sm">
                  {index + 1}
                </div>
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
                  <p className="text-sm text-gray-500">{product.brand} • {product.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">${product.currentPrice || product.lowestPrice}</p>
                  <p className={`text-sm font-medium ${product.priceChange && product.priceChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {product.priceChange && product.priceChange < 0 ? '↓' : '↑'} {Math.abs(product.priceChange || 0)}%
                  </p>
                </div>
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{product.retailers?.length || 0}</p>
                    <p className="text-xs text-gray-500">Retailers</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{Math.floor(Math.random() * 500) + 100}</p>
                    <p className="text-xs text-gray-500">Views</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{Math.floor(Math.random() * 50) + 10}</p>
                    <p className="text-xs text-gray-500">Alerts</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Performance */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Category Performance</h3>
          <div className="space-y-4">
            {Object.entries(analyticsData.categoryStats).map(([category, data]) => {
              const percentage = Math.round((data.count / productsData.length) * 100);
              const colors = ['bg-teal-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500'];
              const colorIndex = Object.keys(analyticsData.categoryStats).indexOf(category) % colors.length;
              return (
                <div key={category}>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{category}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500">{data.count} products</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600">${data.totalValue.toLocaleString()}</span>
                      <span className="text-green-600 text-xs">↓ {data.avgDiscount}% avg</span>
                    </div>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${colors[colorIndex]} rounded-full transition-all`} style={{ width: `${percentage}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Brand Distribution */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Brand Distribution</h3>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(analyticsData.brandStats).map(([brand, data]) => (
              <div key={brand} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    <i className="ri-building-2-line text-gray-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{brand}</p>
                    <p className="text-xs text-gray-500">{data.count} products</p>
                  </div>
                </div>
                <p className="text-lg font-semibold text-teal-600">${data.totalValue.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Price Trends */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-gray-900">Price Trends Overview</h3>
            <p className="text-sm text-gray-500 mt-1">Weekly price movement across all products</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">7D</button>
            <button className="px-3 py-1.5 text-sm font-medium bg-teal-100 text-teal-700 rounded-lg cursor-pointer whitespace-nowrap">30D</button>
            <button className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">90D</button>
          </div>
        </div>
        <div className="h-64 flex items-end justify-between gap-2">
          {['Week 1', 'Week 2', 'Week 3', 'Week 4'].map((week, i) => {
            const drops = [35, 42, 28, 45][i];
            const rises = [15, 18, 22, 12][i];
            return (
              <div key={week} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex gap-1 h-48">
                  <div className="flex-1 flex flex-col justify-end">
                    <div 
                      className="w-full bg-gradient-to-t from-green-500 to-green-400 rounded-t-lg transition-all hover:from-green-600 hover:to-green-500"
                      style={{ height: `${drops * 2}%` }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-end">
                    <div 
                      className="w-full bg-gradient-to-t from-red-400 to-red-300 rounded-t-lg transition-all hover:from-red-500 hover:to-red-400"
                      style={{ height: `${rises * 2}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-gray-500">{week}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-600">Price Drops</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-400 rounded"></div>
            <span className="text-sm text-gray-600">Price Increases</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmails = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
          <p className="text-sm text-gray-500 mt-1">Manage notification email templates for price alerts</p>
        </div>
        <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap flex items-center gap-2">
          <i className="ri-add-line"></i>
          New Template
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-mail-line text-teal-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{emailTemplates.length}</p>
              <p className="text-xs text-gray-500">Total Templates</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-check-line text-green-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{emailTemplates.filter(t => t.isActive).length}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <i className="ri-notification-3-line text-amber-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{emailTemplates.filter(t => t.category === 'alerts').length}</p>
              <p className="text-xs text-gray-500">Alert Templates</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <i className="ri-send-plane-line text-cyan-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">1,247</p>
              <p className="text-xs text-gray-500">Sent This Week</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setTemplateFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
            templateFilter === 'all' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Templates
        </button>
        {emailCategories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setTemplateFilter(cat.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center gap-2 ${
              templateFilter === cat.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={cat.icon}></i>
            {cat.name}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTemplates.map(template => {
          const category = emailCategories.find(c => c.id === template.category);
          return (
            <div key={template.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category?.color || 'bg-gray-100 text-gray-600'}`}>
                      <i className={category?.icon || 'ri-mail-line'}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <p className="text-xs text-gray-500">{template.slug}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    template.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {template.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                
                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-gray-500 mb-1">Subject Line</p>
                  <p className="text-sm text-gray-900 font-medium">{template.subject}</p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-time-line"></i>
                    <span>Modified {template.lastModified}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowPreviewModal(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      title="Preview"
                    >
                      <i className="ri-eye-line text-gray-500"></i>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowTemplateEditor(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                      title="Edit"
                    >
                      <i className="ri-edit-line text-gray-500"></i>
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors" title="Duplicate">
                      <i className="ri-file-copy-line text-gray-500"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Variables */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Variables ({template.variables.length})</p>
                <div className="flex flex-wrap gap-1">
                  {template.variables.slice(0, 5).map(variable => (
                    <span key={variable} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">
                      {`{{${variable}}}`}
                    </span>
                  ))}
                  {template.variables.length > 5 && (
                    <span className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-600">
                      +{template.variables.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedNotification.color}`}>
                  <i className={selectedNotification.icon}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedNotification.title}</h3>
                  <p className="text-xs text-gray-500">{formatNotificationTime(selectedNotification.time)}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNotificationDetail(false);
                  setSelectedNotification(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            
            <div className="p-4 bg-gray-100 overflow-y-auto max-h-[70vh]">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div dangerouslySetInnerHTML={{ __html: selectedTemplate.htmlContent }} />
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowNotificationDetail(false);
                  setSelectedNotification(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
              {selectedNotification.type === 'activity' && (
                <button
                  onClick={() => {
                    setShowNotificationDetail(false);
                    setSelectedNotification(null);
                    setActiveTab('activity');
                    setShowNotifications(false);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                >
                  View in Activity Logs
                </button>
              )}
              {selectedNotification.type === 'alert' && (
                <button
                  onClick={() => {
                    setShowNotificationDetail(false);
                    setSelectedNotification(null);
                    setActiveTab('alerts');
                    setShowNotifications(false);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                >
                  View in Alerts
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {showTemplateEditor && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Edit Template</h3>
                <p className="text-sm text-gray-500">{selectedTemplate.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowTemplateEditor(false);
                  setSelectedTemplate(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    defaultValue={selectedTemplate.name}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    defaultValue={selectedTemplate.category}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {emailCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                <input
                  type="text"
                  defaultValue={selectedTemplate.subject}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  defaultValue={selectedTemplate.description}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTML Content</label>
                <textarea
                  defaultValue={selectedTemplate.htmlContent}
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" defaultChecked={selectedTemplate.isActive} className="w-4 h-4 text-teal-600 rounded" />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowTemplateEditor(false);
                  setSelectedTemplate(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderActivity = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">User Activity Logs</h2>
          <p className="text-sm text-gray-500 mt-1">Track user interactions with price alerts</p>
        </div>
        <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-line"></i>
          Export Logs
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-history-line text-teal-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activityStats.total}</p>
              <p className="text-xs text-gray-500">Total Events</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-add-circle-line text-green-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activityStats.alertsCreated}</p>
              <p className="text-xs text-gray-500">Alerts Created</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <i className="ri-notification-3-line text-amber-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activityStats.alertsTriggered}</p>
              <p className="text-xs text-gray-500">Alerts Triggered</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
              <i className="ri-mail-send-line text-cyan-600"></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activityStats.emailsSent}</p>
              <p className="text-xs text-gray-500">Emails Sent</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={activitySearchQuery}
                onChange={(e) => setActivitySearchQuery(e.target.value)}
                placeholder="Search by user, email, or product..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <select
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Actions</option>
            {actionTypes.map(action => (
              <option key={action.id} value={action.id}>{action.label}</option>
            ))}
          </select>
          <select
            value={activityDateRange}
            onChange={(e) => setActivityDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="24hours">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <p className="text-sm text-gray-600">
            Showing <span className="font-medium text-gray-900">{filteredActivityLogs.length}</span> events
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredActivityLogs.map(log => {
            const actionStyle = getActionStyle(log.action);
            return (
              <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${actionStyle.color}`}>
                    <i className={actionStyle.icon}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{log.userName}</span>
                          <span className="text-gray-500"> {log.actionLabel.toLowerCase()}</span>
                          {log.productName && (
                            <span className="text-gray-500"> for </span>
                          )}
                          {log.productName && (
                            <span className="font-medium text-teal-600">{log.productName}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{log.userEmail}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-gray-500">{formatTimestamp(log.timestamp)}</p>
                        <p className="text-xs text-gray-400 mt-1">{log.ipAddress}</p>
                      </div>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(log.details).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                            {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {filteredActivityLogs.length === 0 && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-history-line text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No activity found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderProducts = () => (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h3 className="font-semibold text-gray-900">Products Management</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
                />
              </div>
              {selectedProducts.length > 0 && (
                <>
                  <span className="text-sm text-gray-500">{selectedProducts.length} selected</span>
                  <button 
                    onClick={() => setShowBulkEditModal(true)}
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
                  >
                    Bulk Edit
                  </button>
                  <button 
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 cursor-pointer whitespace-nowrap"
                  >
                    Delete
                  </button>
                </>
              )}
              <button 
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                Import CSV
              </button>
              <button 
                onClick={() => setShowAddProductModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredAdminProducts.length && filteredAdminProducts.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-teal-600 rounded cursor-pointer"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price Range</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAdminProducts.map(product => (
                <tr key={product.id} className={`hover:bg-gray-50 ${selectedProducts.includes(product.id) ? 'bg-teal-50' : ''}`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                      className="w-4 h-4 text-teal-600 rounded cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                        <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.brand}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">{product.category}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">${product.lowestPrice} - ${product.highestPrice}</td>
                  <td className="px-6 py-4">
                    <span className={`text-sm font-medium ${product.priceChange && product.priceChange < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.priceChange && product.priceChange < 0 ? '↓' : '↑'} {Math.abs(product.priceChange || 0)}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleEditProduct(product)}
                        className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                      >
                        <i className="ri-edit-line text-gray-500"></i>
                      </button>
                      <button className="p-2 hover:bg-red-50 rounded-lg cursor-pointer">
                        <i className="ri-delete-bin-line text-red-500"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderUsers = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h3 className="font-semibold text-gray-900">Users Management</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search users..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alerts</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {mockUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
                      {user.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.alerts}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{user.joined}</td>
                <td className="px-6 py-4">
                  <button className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                    <i className="ri-eye-line text-gray-500"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Price Alerts Overview</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {priceDropNotifications.map(alert => (
              <tr key={alert.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                      <img src={alert.productImage} alt={alert.productName} className="w-full h-full object-contain" />
                    </div>
                    <p className="font-medium text-gray-900 text-sm">{alert.productName}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">${alert.targetPrice}</td>
                <td className="px-6 py-4 text-sm text-gray-600">${alert.droppedPrice || alert.originalPrice}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    alert.status === 'triggered' ? 'bg-green-100 text-green-700' :
                    alert.status === 'active' ? 'bg-teal-100 text-teal-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {alert.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{new Date(alert.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // App Settings state
  const [appSettings, setAppSettings] = useState({
    siteName: 'Ohprice',
    siteTagline: 'Your Smart Price Tracking Companion',
    siteLogo: '',
    siteFavicon: '',
    defaultCurrency: 'USD',
    defaultLanguage: 'en',
    contactEmail: 'support@ohprice.com',
    contactPhone: '+1 (555) 123-4567',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    socialLinks: {
      facebook: 'https://facebook.com/ohprice',
      twitter: 'https://twitter.com/ohprice',
      instagram: 'https://instagram.com/ohprice',
      linkedin: 'https://linkedin.com/company/ohprice',
    },
    businessAddress: '123 Tech Street, San Francisco, CA 94105',
    enableNotifications: true,
    enableNewsletter: true,
    maintenanceMode: false,
    // Theme settings
    darkMode: false,
    themeColors: {
      primaryColor: '#0d9488',
      secondaryColor: '#10b981',
      accentColor: '#f59e0b',
      backgroundColor: '#f9fafb',
      textColor: '#111827',
      headerBgColor: '#ffffff',
      footerBgColor: '#1f2937',
    },
    customCSS: '',
    fontFamily: 'Inter',
    activePreset: 'custom',
    emailNotifyOnChange: true,
    notificationEmail: 'admin@ohprice.com',
  });
  const [appSettingsSaved, setAppSettingsSaved] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [backupData, setBackupData] = useState('');
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreStatus, setRestoreStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'ar', name: 'Arabic' },
  ];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Dubai',
    'Australia/Sydney',
  ];

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (DE)' },
  ];

  const fontFamilies = [
    { value: 'Inter', label: 'Inter' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Raleway', label: 'Raleway' },
  ];

  // Preset theme templates
  const presetThemes = [
    {
      id: 'default',
      name: 'Default Teal',
      description: 'Clean and professional teal theme',
      colors: {
        primaryColor: '#0d9488',
        secondaryColor: '#10b981',
        accentColor: '#f59e0b',
        backgroundColor: '#f9fafb',
        textColor: '#111827',
        headerBgColor: '#ffffff',
        footerBgColor: '#1f2937',
      },
      darkColors: {
        primaryColor: '#14b8a6',
        secondaryColor: '#34d399',
        accentColor: '#fbbf24',
        backgroundColor: '#111827',
        textColor: '#f9fafb',
        headerBgColor: '#1f2937',
        footerBgColor: '#030712',
      },
    },
    {
      id: 'ocean',
      name: 'Ocean Breeze',
      description: 'Calm and refreshing ocean-inspired',
      colors: {
        primaryColor: '#0891b2',
        secondaryColor: '#06b6d4',
        accentColor: '#f97316',
        backgroundColor: '#f0fdfa',
        textColor: '#164e63',
        headerBgColor: '#ffffff',
        footerBgColor: '#155e75',
      },
      darkColors: {
        primaryColor: '#22d3ee',
        secondaryColor: '#67e8f9',
        accentColor: '#fb923c',
        backgroundColor: '#0c4a6e',
        textColor: '#e0f2fe',
        headerBgColor: '#164e63',
        footerBgColor: '#082f49',
      },
    },
    {
      id: 'forest',
      name: 'Forest Green',
      description: 'Natural and earthy green tones',
      colors: {
        primaryColor: '#15803d',
        secondaryColor: '#22c55e',
        accentColor: '#eab308',
        backgroundColor: '#f0fdf4',
        textColor: '#14532d',
        headerBgColor: '#ffffff',
        footerBgColor: '#166534',
      },
      darkColors: {
        primaryColor: '#4ade80',
        secondaryColor: '#86efac',
        accentColor: '#facc15',
        backgroundColor: '#14532d',
        textColor: '#dcfce7',
        headerBgColor: '#166534',
        footerBgColor: '#052e16',
      },
    },
    {
      id: 'sunset',
      name: 'Warm Sunset',
      description: 'Warm and inviting sunset colors',
      colors: {
        primaryColor: '#ea580c',
        secondaryColor: '#f97316',
        accentColor: '#dc2626',
        backgroundColor: '#fff7ed',
        textColor: '#7c2d12',
        headerBgColor: '#ffffff',
        footerBgColor: '#9a3412',
      },
      darkColors: {
        primaryColor: '#fb923c',
        secondaryColor: '#fdba74',
        accentColor: '#f87171',
        backgroundColor: '#7c2d12',
        textColor: '#ffedd5',
        headerBgColor: '#9a3412',
        footerBgColor: '#431407',
      },
    },
    {
      id: 'rose',
      name: 'Rose Garden',
      description: 'Elegant and sophisticated rose',
      colors: {
        primaryColor: '#be185d',
        secondaryColor: '#ec4899',
        accentColor: '#8b5cf6',
        backgroundColor: '#fdf2f8',
        textColor: '#831843',
        headerBgColor: '#ffffff',
        footerBgColor: '#9d174d',
      },
      darkColors: {
        primaryColor: '#f472b6',
        secondaryColor: '#f9a8d4',
        accentColor: '#a78bfa',
        backgroundColor: '#831843',
        textColor: '#fce7f3',
        headerBgColor: '#9d174d',
        footerBgColor: '#500724',
      },
    },
    {
      id: 'slate',
      name: 'Modern Slate',
      description: 'Sleek and modern grayscale',
      colors: {
        primaryColor: '#475569',
        secondaryColor: '#64748b',
        accentColor: '#0ea5e9',
        backgroundColor: '#f8fafc',
        textColor: '#1e293b',
        headerBgColor: '#ffffff',
        footerBgColor: '#334155',
      },
      darkColors: {
        primaryColor: '#94a3b8',
        secondaryColor: '#cbd5e1',
        accentColor: '#38bdf8',
        backgroundColor: '#0f172a',
        textColor: '#f1f5f9',
        headerBgColor: '#1e293b',
        footerBgColor: '#020617',
      },
    },
  ];

  // Apply preset theme
  const applyPresetTheme = (presetId: string) => {
    const preset = presetThemes.find(p => p.id === presetId);
    if (preset) {
      const colors = appSettings.darkMode ? preset.darkColors : preset.colors;
      setAppSettings({
        ...appSettings,
        activePreset: presetId,
        themeColors: colors,
      });
    }
  };

  // Toggle dark mode
  const toggleDarkMode = (enabled: boolean) => {
    const preset = presetThemes.find(p => p.id === appSettings.activePreset);
    if (preset && appSettings.activePreset !== 'custom') {
      const colors = enabled ? preset.darkColors : preset.colors;
      setAppSettings({
        ...appSettings,
        darkMode: enabled,
        themeColors: colors,
      });
    } else {
      setAppSettings({
        ...appSettings,
        darkMode: enabled,
      });
    }
  };

  const handleSaveAppSettings = async () => {
    console.log('Saving app settings:', appSettings);
    
    // Send email notification if enabled
    if (appSettings.emailNotifyOnChange && appSettings.notificationEmail) {
      try {
        const formData = new URLSearchParams();
        formData.append('email', appSettings.notificationEmail);
        formData.append('subject', 'App Settings Changed');
        formData.append('siteName', appSettings.siteName);
        formData.append('changedAt', new Date().toISOString());
        formData.append('changedBy', 'Admin');
        formData.append('settings', JSON.stringify({
          siteName: appSettings.siteName,
          siteTagline: appSettings.siteTagline,
          defaultCurrency: appSettings.defaultCurrency,
          defaultLanguage: appSettings.defaultLanguage,
          darkMode: appSettings.darkMode,
          activePreset: appSettings.activePreset,
          maintenanceMode: appSettings.maintenanceMode,
        }));
        
        await fetch('https://readdy.ai/api/form/d69rh25biueuoqm1s3n0', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        });
      } catch (error) {
        console.error('Failed to send notification email:', error);
      }
    }
    
    setAppSettingsSaved(true);
    setTimeout(() => setAppSettingsSaved(false), 3000);
  };

  // Logo upload handler
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setLogoUploading(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        setAppSettings({ ...appSettings, siteLogo: event.target?.result as string });
        setLogoUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Favicon upload handler
  const handleFaviconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      if (file.size > 1 * 1024 * 1024) {
        alert('File size must be less than 1MB');
        return;
      }
      setFaviconUploading(true);
      const reader = new FileReader();
      reader.onload = (event) => {
        setAppSettings({ ...appSettings, siteFavicon: event.target?.result as string });
        setFaviconUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // Backup settings handler
  const handleBackupSettings = () => {
    const backupObject = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      appSettings,
      alertSettings,
    };
    const jsonString = JSON.stringify(backupObject, null, 2);
    setBackupData(jsonString);
    setShowBackupModal(true);
  };

  // Download backup file
  const downloadBackup = () => {
    const blob = new Blob([backupData], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ohprice-settings-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setShowBackupModal(false);
  };

  // Restore settings handler
  const handleRestoreSettings = () => {
    if (!restoreFile) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result as string);
        if (data.appSettings) {
          setAppSettings(data.appSettings);
        }
        if (data.alertSettings) {
          setAlertSettings(data.alertSettings);
        }
        setRestoreStatus('success');
        setTimeout(() => {
          setShowRestoreModal(false);
          setRestoreStatus('idle');
          setRestoreFile(null);
        }, 2000);
      } catch (error) {
        console.error('Restore error:', error);
        setRestoreStatus('error');
      }
    };
    reader.readAsText(restoreFile);
  };

  const renderAppSettings = () => (
    <div className="space-y-6">
      {/* Permission Check Banner */}
      {!hasPermission('modifyAppSettings') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <i className="ri-lock-line text-amber-600"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">View Only Mode</p>
            <p className="text-xs text-amber-600">You don't have permission to modify app settings. Contact a Super Admin for access.</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {appSettingsSaved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-green-600"></i>
          </div>
          <p className="text-sm text-green-700 font-medium">App settings saved successfully!</p>
        </div>
      )}

      {/* Current User Role Indicator (for demo purposes) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <i className="ri-user-star-line text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Current User Role</h3>
              <p className="text-sm text-gray-500">Switch roles to test permissions (demo only)</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${rolePermissions[currentUserRole].color}`}>
            {rolePermissions[currentUserRole].name}
          </span>
        </div>
        <div className="p-5">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(rolePermissions) as Array<keyof typeof rolePermissions>).map(role => (
              <button
                key={role}
                onClick={() => setCurrentUserRole(role)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  currentUserRole === role
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {rolePermissions[role].name}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            <i className="ri-information-line mr-1"></i>
            In production, user roles would be assigned through user management, not switchable here.
          </p>
        </div>
      </div>

      {/* User Role Permissions */}
      {hasPermission('modifyRoles') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg flex items-center justify-center">
              <i className="ri-shield-user-line text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Role Permissions</h3>
              <p className="text-sm text-gray-500">Configure what each role can access and modify</p>
            </div>
          </div>
          <div className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Permission</th>
                    {(Object.keys(rolePermissions) as Array<keyof typeof rolePermissions>).map(role => (
                      <th key={role} className="text-center py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${rolePermissions[role].color}`}>
                          {rolePermissions[role].name}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permissionLabels).map(([key, { label, description, icon }]) => (
                    <tr key={key} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            <i className={`${icon} text-gray-600`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{label}</p>
                            <p className="text-xs text-gray-500">{description}</p>
                          </div>
                        </div>
                      </td>
                      {(Object.keys(rolePermissions) as Array<keyof typeof rolePermissions>).map(role => (
                        <td key={role} className="text-center py-3 px-4">
                          {role === 'super_admin' ? (
                            <div className="flex items-center justify-center">
                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                <i className="ri-check-line text-green-600 text-sm"></i>
                              </div>
                            </div>
                          ) : (
                            <label className="flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={rolePermissions[role].permissions[key as keyof typeof rolePermissions.super_admin.permissions]}
                                onChange={(e) => updateRolePermission(role, key, e.target.checked)}
                                className="sr-only"
                              />
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                                rolePermissions[role].permissions[key as keyof typeof rolePermissions.super_admin.permissions]
                                  ? 'bg-green-100'
                                  : 'bg-gray-100'
                              }`}>
                                <i className={`text-sm ${
                                  rolePermissions[role].permissions[key as keyof typeof rolePermissions.super_admin.permissions]
                                    ? 'ri-check-line text-green-600'
                                    : 'ri-close-line text-gray-400'
                                }`}></i>
                              </div>
                            </label>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              <i className="ri-information-line mr-1"></i>
              Super Admin permissions cannot be modified. Changes to other roles take effect immediately.
            </p>
          </div>
        </div>
      )}

      {/* View-only notice for non-role-modifiers */}
      {!hasPermission('modifyRoles') && hasPermission('modifyAppSettings') && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="ri-shield-user-line text-gray-500"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">Role Permissions</p>
            <p className="text-xs text-gray-500">Only Super Admins can modify role permissions.</p>
          </div>
        </div>
      )}

      {/* Backup/Restore Actions */}
      {hasPermission('backupRestore') && (
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => setShowRestoreModal(true)}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2 transition-colors"
          >
            <i className="ri-upload-2-line"></i>
            Restore Settings
          </button>
          <button
            onClick={handleBackupSettings}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2 transition-colors"
          >
            <i className="ri-download-2-line"></i>
            Backup Settings
          </button>
        </div>
      )}

      {/* Site Identity & Logo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <i className="ri-global-line text-teal-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Site Identity</h3>
            <p className="text-sm text-gray-500">Basic information and branding</p>
          </div>
        </div>
        <div className={`p-5 space-y-5 ${!hasPermission('modifyAppSettings') ? 'opacity-60 pointer-events-none' : ''}`}>
          {/* Logo Upload Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Logo</label>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                  {appSettings.siteLogo ? (
                    <img src={appSettings.siteLogo} alt="Site Logo" className="w-full h-full object-contain" />
                  ) : (
                    <i className="ri-image-line text-2xl text-gray-400"></i>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={!hasPermission('modifyAppSettings')}
                  />
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading || !hasPermission('modifyAppSettings')}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {logoUploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-line"></i>
                        Upload Logo
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">Recommended: 200x60px, PNG or SVG, max 5MB</p>
                  {appSettings.siteLogo && hasPermission('modifyAppSettings') && (
                    <button
                      onClick={() => setAppSettings({ ...appSettings, siteLogo: '' })}
                      className="text-xs text-red-600 hover:text-red-700 mt-1 cursor-pointer"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                  {appSettings.siteFavicon ? (
                    <img src={appSettings.siteFavicon} alt="Favicon" className="w-full h-full object-contain" />
                  ) : (
                    <i className="ri-image-line text-xl text-gray-400"></i>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={faviconInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFaviconUpload}
                    className="hidden"
                    disabled={!hasPermission('modifyAppSettings')}
                  />
                  <button
                    onClick={() => faviconInputRef.current?.click()}
                    disabled={faviconUploading || !hasPermission('modifyAppSettings')}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {faviconUploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-line"></i>
                        Upload Favicon
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">32x32px or 64x64px, ICO/PNG, max 1MB</p>
                  {appSettings.siteFavicon && hasPermission('modifyAppSettings') && (
                    <button
                      onClick={() => setAppSettings({ ...appSettings, siteFavicon: '' })}
                      className="text-xs text-red-600 hover:text-red-700 mt-1 cursor-pointer"
                    >
                      Remove favicon
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                value={appSettings.siteName}
                onChange={(e) => setAppSettings({ ...appSettings, siteName: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Enter site name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site Tagline</label>
              <input
                type="text"
                value={appSettings.siteTagline}
                onChange={(e) => setAppSettings({ ...appSettings, siteTagline: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Enter site tagline"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dark Mode & Theme Presets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-gray-600 rounded-lg flex items-center justify-center">
              <i className="ri-moon-line text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Dark Mode & Theme Presets</h3>
              <p className="text-sm text-gray-500">Choose a preset theme or customize your own</p>
            </div>
          </div>
          {/* Dark Mode Toggle */}
          <div className={`flex items-center gap-3 ${!hasPermission('modifyTheme') ? 'opacity-60 pointer-events-none' : ''}`}>
            <span className="text-sm text-gray-600">
              <i className="ri-sun-line mr-1"></i>
              Light
            </span>
            <label className="relative cursor-pointer">
              <input
                type="checkbox"
                checked={appSettings.darkMode}
                onChange={(e) => toggleDarkMode(e.target.checked)}
                disabled={!hasPermission('modifyTheme')}
                className="sr-only"
              />
              <div className={`w-14 h-7 rounded-full transition-colors ${appSettings.darkMode ? 'bg-gray-800' : 'bg-gray-300'}`}>
                <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform flex items-center justify-center ${appSettings.darkMode ? 'translate-x-7' : 'translate-x-0.5'}`}>
                  <i className={`text-xs ${appSettings.darkMode ? 'ri-moon-fill text-gray-800' : 'ri-sun-fill text-amber-500'}`}></i>
                </div>
              </div>
            </label>
            <span className="text-sm text-gray-600">
              <i className="ri-moon-fill mr-1"></i>
              Dark
            </span>
          </div>
        </div>
        <div className={`p-5 ${!hasPermission('modifyTheme') ? 'opacity-60 pointer-events-none' : ''}`}>
          <label className="block text-sm font-medium text-gray-700 mb-3">Theme Presets</label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {presetThemes.map(preset => {
              const colors = appSettings.darkMode ? preset.darkColors : preset.colors;
              return (
                <button
                  key={preset.id}
                  onClick={() => applyPresetTheme(preset.id)}
                  disabled={!hasPermission('modifyTheme')}
                  className={`p-3 rounded-xl border-2 transition-all cursor-pointer text-left disabled:cursor-not-allowed ${
                    appSettings.activePreset === preset.id 
                      ? 'border-teal-500 bg-teal-50 shadow-md' 
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.primaryColor }}></div>
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.secondaryColor }}></div>
                    <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.accentColor }}></div>
                  </div>
                  <p className="text-xs font-medium text-gray-900 truncate">{preset.name}</p>
                  <p className="text-xs text-gray-500 truncate">{preset.description}</p>
                  {appSettings.activePreset === preset.id && (
                    <div className="mt-2 flex items-center gap-1 text-teal-600">
                      <i className="ri-check-line text-xs"></i>
                      <span className="text-xs font-medium">Active</span>
                    </div>
                  )}
                </button>
              );
            })}
            {/* Custom Theme Option */}
            <button
              onClick={() => setAppSettings({ ...appSettings, activePreset: 'custom' })}
              disabled={!hasPermission('modifyTheme')}
              className={`p-3 rounded-xl border-2 transition-all cursor-pointer text-left disabled:cursor-not-allowed ${
                appSettings.activePreset === 'custom' 
                  ? 'border-teal-500 bg-teal-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <div className="flex gap-1 mb-2">
                <div className="w-5 h-5 rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center">
                  <i className="ri-add-line text-xs text-gray-400"></i>
                </div>
              </div>
              <p className="text-xs font-medium text-gray-900">Custom</p>
              <p className="text-xs text-gray-500">Create your own</p>
              {appSettings.activePreset === 'custom' && (
                <div className="mt-2 flex items-center gap-1 text-teal-600">
                  <i className="ri-check-line text-xs"></i>
                  <span className="text-xs font-medium">Active</span>
                </div>
              )}
            </button>
          </div>
          
          {/* Dark Mode Preview */}
          <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: appSettings.darkMode ? '#1f2937' : '#f3f4f6' }}>
            <p className="text-xs mb-2" style={{ color: appSettings.darkMode ? '#9ca3af' : '#6b7280' }}>
              {appSettings.darkMode ? 'Dark Mode Preview' : 'Light Mode Preview'}
            </p>
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: appSettings.themeColors.primaryColor }}>
                Primary
              </div>
              <div className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: appSettings.themeColors.secondaryColor }}>
                Secondary
              </div>
              <div className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: appSettings.themeColors.accentColor }}>
                Accent
              </div>
              <span className="text-sm" style={{ color: appSettings.themeColors.textColor }}>
                Sample Text
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Theme & Appearance - Only show if user has theme permission */}
      {hasPermission('modifyTheme') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-400 rounded-lg flex items-center justify-center">
              <i className="ri-palette-line text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Custom Theme Colors</h3>
              <p className="text-sm text-gray-500">Fine-tune colors for your custom theme</p>
            </div>
          </div>
          <div className="p-5 space-y-5">
            {/* Color Pickers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Brand Colors</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Primary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.primaryColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, primaryColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.primaryColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, primaryColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Secondary Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.secondaryColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, secondaryColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.secondaryColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, secondaryColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Accent Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.accentColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, accentColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.accentColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, accentColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.textColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, textColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.textColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, textColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Layout Colors */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Layout Colors</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.backgroundColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, backgroundColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.backgroundColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, backgroundColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Header Background</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.headerBgColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, headerBgColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.headerBgColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, headerBgColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Footer Background</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.themeColors.footerBgColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, footerBgColor: e.target.value }
                      })}
                      className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.themeColors.footerBgColor}
                      onChange={(e) => setAppSettings({
                        ...appSettings,
                        activePreset: 'custom',
                        themeColors: { ...appSettings.themeColors, footerBgColor: e.target.value }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Font Family */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
              <select
                value={appSettings.fontFamily}
                onChange={(e) => setAppSettings({ ...appSettings, fontFamily: e.target.value })}
                className="w-full md:w-64 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              >
                {fontFamilies.map(font => (
                  <option key={font.value} value={font.value}>{font.label}</option>
                ))}
              </select>
            </div>

            {/* Theme Preview */}
            <div className="mt-4 p-4 bg-gray-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-3">Theme Preview</p>
              <div 
                className="rounded-lg overflow-hidden border border-gray-200"
                style={{ backgroundColor: appSettings.themeColors.backgroundColor }}
              >
                <div 
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ backgroundColor: appSettings.themeColors.headerBgColor }}
                >
                  <span className="font-semibold" style={{ color: appSettings.themeColors.primaryColor, fontFamily: appSettings.fontFamily }}>
                    {appSettings.siteName}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: appSettings.themeColors.primaryColor, color: '#fff' }}>Button</span>
                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: appSettings.themeColors.secondaryColor, color: '#fff' }}>Secondary</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm mb-2" style={{ color: appSettings.themeColors.textColor, fontFamily: appSettings.fontFamily }}>
                    This is how your content will look with the selected theme colors.
                  </p>
                  <span className="text-xs" style={{ color: appSettings.themeColors.accentColor }}>Accent text example</span>
                </div>
                <div 
                  className="px-4 py-3"
                  style={{ backgroundColor: appSettings.themeColors.footerBgColor }}
                >
                  <span className="text-xs text-white opacity-70">Footer area</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS - Only show if user has theme permission */}
      {hasPermission('modifyTheme') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
              <i className="ri-code-s-slash-line text-white"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Custom CSS</h3>
              <p className="text-sm text-gray-500">Add custom styles to override default styling</p>
            </div>
          </div>
          <div className="p-5">
            <div className="relative">
              <textarea
                value={appSettings.customCSS}
                onChange={(e) => setAppSettings({ ...appSettings, customCSS: e.target.value })}
                rows={8}
                placeholder={`/* Add your custom CSS here */\n\n.custom-class {\n  color: #333;\n  font-size: 14px;\n}`}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none bg-gray-900 text-green-400"
                style={{ tabSize: 2 }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              <i className="ri-information-line mr-1"></i>
              Custom CSS will be applied globally. Use with caution.
            </p>
          </div>
        </div>
      )}

      {/* Email Notification Settings */}
      {hasPermission('modifyAppSettings') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <i className="ri-mail-settings-line text-indigo-600"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Settings Change Notifications</h3>
              <p className="text-sm text-gray-500">Get notified when app settings are modified</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                  <i className="ri-notification-badge-line text-indigo-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Email Notification on Settings Change</p>
                  <p className="text-xs text-gray-500">Receive an email whenever settings are saved</p>
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={appSettings.emailNotifyOnChange}
                  onChange={(e) => setAppSettings({ ...appSettings, emailNotifyOnChange: e.target.checked })}
                  className="sr-only"
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${appSettings.emailNotifyOnChange ? 'bg-teal-600' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${appSettings.emailNotifyOnChange ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
                </div>
              </div>
            </label>

            {appSettings.emailNotifyOnChange && (
              <div className="pl-4 border-l-2 border-teal-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notification Email Address</label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="email"
                    value={appSettings.notificationEmail}
                    onChange={(e) => setAppSettings({ ...appSettings, notificationEmail: e.target.value })}
                    className="w-full md:w-96 pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="admin@example.com"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="ri-information-line mr-1"></i>
                  This email will receive notifications about settings changes including site name, theme, and feature toggles.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Regional Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <i className="ri-earth-line text-emerald-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Regional Settings</h3>
            <p className="text-sm text-gray-500">Currency, language, and timezone preferences</p>
          </div>
        </div>
        <div className={`p-5 space-y-4 ${!hasPermission('modifyAppSettings') ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Currency</label>
              <select
                value={appSettings.defaultCurrency}
                onChange={(e) => setAppSettings({ ...appSettings, defaultCurrency: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {currencies.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
              <select
                value={appSettings.defaultLanguage}
                onChange={(e) => setAppSettings({ ...appSettings, defaultLanguage: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {languages.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                value={appSettings.timezone}
                onChange={(e) => setAppSettings({ ...appSettings, timezone: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
              <select
                value={appSettings.dateFormat}
                onChange={(e) => setAppSettings({ ...appSettings, dateFormat: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {dateFormats.map(format => (
                  <option key={format.value} value={format.value}>{format.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Preview */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">Preview</p>
            <div className="flex items-center gap-6 text-sm">
              <span className="text-gray-700">
                <span className="text-gray-500">Price:</span> {currencies.find(c => c.code === appSettings.defaultCurrency)?.symbol}1,234.56
              </span>
              <span className="text-gray-700">
                <span className="text-gray-500">Language:</span> {languages.find(l => l.code === appSettings.defaultLanguage)?.name}
              </span>
              <span className="text-gray-700">
                <span className="text-gray-500">Date:</span> {appSettings.dateFormat === 'MM/DD/YYYY' ? '12/25/2024' : appSettings.dateFormat === 'DD/MM/YYYY' ? '25/12/2024' : appSettings.dateFormat === 'YYYY-MM-DD' ? '2024-12-25' : '25.12.2024'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <i className="ri-contacts-line text-amber-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Contact Information</h3>
            <p className="text-sm text-gray-500">How customers can reach you</p>
          </div>
        </div>
        <div className={`p-5 space-y-4 ${!hasPermission('modifyAppSettings') ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="email"
                  value={appSettings.contactEmail}
                  onChange={(e) => setAppSettings({ ...appSettings, contactEmail: e.target.value })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="support@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <div className="relative">
                <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="tel"
                  value={appSettings.contactPhone}
                  onChange={(e) => setAppSettings({ ...appSettings, contactPhone: e.target.value })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
            <div className="relative">
              <i className="ri-map-pin-line absolute left-3 top-3 text-gray-400"></i>
              <textarea
                value={appSettings.businessAddress}
                onChange={(e) => setAppSettings({ ...appSettings, businessAddress: e.target.value })}
                disabled={!hasPermission('modifyAppSettings')}
                rows={2}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="Enter your business address"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Social Media Links */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
            <i className="ri-share-line text-rose-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Social Media Links</h3>
            <p className="text-sm text-gray-500">Connect your social media profiles</p>
          </div>
        </div>
        <div className={`p-5 ${!hasPermission('modifyAppSettings') ? 'opacity-60 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
              <div className="relative">
                <i className="ri-facebook-fill absolute left-3 top-1/2 -translate-y-1/2 text-[#1877F2]"></i>
                <input
                  type="url"
                  value={appSettings.socialLinks.facebook}
                  onChange={(e) => setAppSettings({ ...appSettings, socialLinks: { ...appSettings.socialLinks, facebook: e.target.value } })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="https://facebook.com/yourpage"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twitter / X</label>
              <div className="relative">
                <i className="ri-twitter-x-fill absolute left-3 top-1/2 -translate-y-1/2 text-gray-900"></i>
                <input
                  type="url"
                  value={appSettings.socialLinks.twitter}
                  onChange={(e) => setAppSettings({ ...appSettings, socialLinks: { ...appSettings.socialLinks, twitter: e.target.value } })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="https://twitter.com/yourhandle"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
              <div className="relative">
                <i className="ri-instagram-fill absolute left-3 top-1/2 -translate-y-1/2 text-[#E4405F]"></i>
                <input
                  type="url"
                  value={appSettings.socialLinks.instagram}
                  onChange={(e) => setAppSettings({ ...appSettings, socialLinks: { ...appSettings.socialLinks, instagram: e.target.value } })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="https://instagram.com/yourprofile"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
              <div className="relative">
                <i className="ri-linkedin-fill absolute left-3 top-1/2 -translate-y-1/2 text-[#0A66C2]"></i>
                <input
                  type="url"
                  value={appSettings.socialLinks.linkedin}
                  onChange={(e) => setAppSettings({ ...appSettings, socialLinks: { ...appSettings.socialLinks, linkedin: e.target.value } })}
                  disabled={!hasPermission('modifyAppSettings')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="https://linkedin.com/company/yourcompany"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
            <i className="ri-toggle-line text-cyan-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Feature Toggles</h3>
            <p className="text-sm text-gray-500">Enable or disable site features</p>
          </div>
        </div>
        <div className={`p-5 space-y-4 ${!hasPermission('modifyAppSettings') ? 'opacity-60 pointer-events-none' : ''}`}>
          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <i className="ri-notification-3-line text-gray-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Push Notifications</p>
                <p className="text-xs text-gray-500">Send push notifications to users</p>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={appSettings.enableNotifications}
                onChange={(e) => setAppSettings({ ...appSettings, enableNotifications: e.target.checked })}
                disabled={!hasPermission('modifyAppSettings')}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${appSettings.enableNotifications ? 'bg-teal-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${appSettings.enableNotifications ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
              </div>
            </div>
          </label>

          <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <i className="ri-mail-send-line text-gray-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Newsletter Subscription</p>
                <p className="text-xs text-gray-500">Allow users to subscribe to newsletter</p>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={appSettings.enableNewsletter}
                onChange={(e) => setAppSettings({ ...appSettings, enableNewsletter: e.target.checked })}
                disabled={!hasPermission('modifyAppSettings')}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${appSettings.enableNewsletter ? 'bg-teal-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${appSettings.enableNewsletter ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
              </div>
            </div>
          </label>

          <label className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors ${hasPermission('maintenanceMode') ? 'bg-red-50 hover:bg-red-100' : 'bg-gray-50 opacity-60'}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <i className="ri-tools-line text-red-600"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Maintenance Mode</p>
                <p className="text-xs text-gray-500">
                  {hasPermission('maintenanceMode') 
                    ? 'Show maintenance page to visitors' 
                    : 'Only Super Admins can toggle maintenance mode'}
                </p>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={appSettings.maintenanceMode}
                onChange={(e) => setAppSettings({ ...appSettings, maintenanceMode: e.target.checked })}
                disabled={!hasPermission('maintenanceMode')}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${appSettings.maintenanceMode ? 'bg-red-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${appSettings.maintenanceMode ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      {hasPermission('modifyAppSettings') && (
        <div className="flex items-center justify-between pt-4">
          <p className="text-sm text-gray-500">
            <i className="ri-information-line mr-1"></i>
            Changes will apply to all pages across the site
          </p>
          <button 
            onClick={handleSaveAppSettings}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap flex items-center gap-2 transition-colors"
          >
            <i className="ri-save-line"></i>
            Save App Settings
          </button>
        </div>
      )}

      {/* Backup Modal */}
      {showBackupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-download-2-line text-teal-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Backup Settings</h3>
                  <p className="text-sm text-gray-500">Download your settings as a JSON file</p>
                </div>
              </div>
              <button
                onClick={() => setShowBackupModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            <div className="p-5">
              <div className="bg-gray-900 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{backupData}</pre>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                <i className="ri-information-line mr-1"></i>
                This backup includes all app settings, alert configurations, and role permissions.
              </p>
            </div>
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowBackupModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(backupData);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-file-copy-line"></i>
                Copy to Clipboard
              </button>
              <button
                onClick={downloadBackup}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-download-line"></i>
                Download Backup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <i className="ri-upload-2-line text-amber-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Restore Settings</h3>
                  <p className="text-sm text-gray-500">Upload a backup file to restore settings</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestoreFile(null);
                  setRestoreStatus('idle');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            <div className="p-5">
              {restoreStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Settings Restored!</h4>
                  <p className="text-sm text-gray-500">Your settings have been successfully restored.</p>
                </div>
              ) : restoreStatus === 'error' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-error-warning-line text-3xl text-red-600"></i>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Restore Failed</h4>
                  <p className="text-sm text-gray-500">Invalid backup file. Please try again.</p>
                  <button
                    onClick={() => {
                      setRestoreStatus('idle');
                      setRestoreFile(null);
                    }}
                    className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <>
                  <div 
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      restoreFile ? 'border-teal-300 bg-teal-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {restoreFile ? (
                      <div>
                        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <i className="ri-file-text-line text-xl text-teal-600"></i>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{restoreFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{(restoreFile.size / 1024).toFixed(2)} KB</p>
                        <button
                          onClick={() => setRestoreFile(null)}
                          className="mt-3 text-xs text-red-600 hover:text-red-700 cursor-pointer"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <i className="ri-upload-cloud-line text-xl text-gray-400"></i>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Drop your backup file here or</p>
                        <label className="inline-block px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap">
                          Browse Files
                          <input
                            type="file"
                            accept=".json"
                            onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-3">Only .json backup files are supported</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                    <i className="ri-error-warning-line text-amber-600 mt-0.5"></i>
                    <p className="text-xs text-amber-700">
                      Warning: Restoring settings will overwrite all current configurations. This action cannot be undone.
                    </p>
                  </div>
                </>
              )}
            </div>
            {restoreStatus === 'idle' && (
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setRestoreFile(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestoreSettings}
                  disabled={!restoreFile}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <i className="ri-refresh-line"></i>
                  Restore Settings
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-900 mb-4">General Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
            <input
              type="text"
              defaultValue="Ohprice"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
            <input
              type="email"
              defaultValue="support@ohprice.com"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <i className="ri-notification-3-line text-amber-600"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Price Alert Threshold Settings</h3>
            <p className="text-sm text-gray-500">Configure default alert thresholds</p>
          </div>
        </div>
        
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Min Threshold (%)</label>
              <input
                type="number"
                value={alertSettings.minThreshold}
                onChange={(e) => setAlertSettings({ ...alertSettings, minThreshold: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Threshold (%)</label>
              <input
                type="number"
                value={alertSettings.maxThreshold}
                onChange={(e) => setAlertSettings({ ...alertSettings, maxThreshold: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Threshold (%)</label>
              <input
                type="number"
                value={alertSettings.defaultThreshold}
                onChange={(e) => setAlertSettings({ ...alertSettings, defaultThreshold: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-900">Enable Auto-Alerts</p>
              <p className="text-xs text-gray-500">Automatically create alerts for tracked products</p>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                checked={alertSettings.enableAutoAlerts}
                onChange={(e) => setAlertSettings({ ...alertSettings, enableAutoAlerts: e.target.checked })}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors ${alertSettings.enableAutoAlerts ? 'bg-teal-600' : 'bg-gray-300'}`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${alertSettings.enableAutoAlerts ? 'translate-x-5' : 'translate-x-0.5'} mt-0.5`}></div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap">
          Save Settings
        </button>
      </div>
    </div>
  );

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-box-line' },
    { id: 'products', label: 'Products', icon: 'ri-shopping-bag-line' },
    { id: 'users', label: 'Users', icon: 'ri-user-line' },
    { id: 'alerts', label: 'Alerts', icon: 'ri-notification-3-line' },
    { id: 'emails', label: 'Email Templates', icon: 'ri-mail-line' },
    { id: 'activity', label: 'Activity Logs', icon: 'ri-history-line' },
    { id: 'app-settings', label: 'App Settings', icon: 'ri-apps-line' },
    { id: 'settings', label: 'Settings', icon: 'ri-settings-3-line' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          {sidebarOpen && (
            <Link to="/" className="text-xl font-bold cursor-pointer" style={{ fontFamily: "Pacifico, serif" }}>
              Ohprice
            </Link>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-800 rounded-lg cursor-pointer transition-colors">
            <i className={`ri-${sidebarOpen ? 'menu-fold' : 'menu-unfold'}-line text-lg`}></i>
          </button>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {tabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    activeTab === tab.id ? 'bg-teal-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  {sidebarOpen && <span className="text-sm font-medium">{tab.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-gray-800">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            {sidebarOpen && <span className="text-sm font-medium">Back to Site</span>}
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900 capitalize">{activeTab === 'emails' ? 'Email Templates' : activeTab === 'activity' ? 'Activity Logs' : activeTab}</h1>
          <div className="flex items-center gap-4">
            {/* Notification Bell with Dropdown */}
            <div className="relative" ref={notificationRef}>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer relative transition-colors"
              >
                <i className="ri-notification-3-line text-gray-600"></i>
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      <p className="text-xs text-gray-500">{unreadCount} unread</p>
                    </div>
                    <button 
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      Mark all as read
                    </button>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {adminNotifications.length > 0 ? (
                      adminNotifications.map(notification => (
                        <div 
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${!notification.read ? 'bg-teal-50/30' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${notification.color}`}>
                              <i className={notification.icon}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5 truncate">{notification.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatNotificationTime(notification.time)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-notification-off-line text-xl text-gray-400"></i>
                        </div>
                        <p className="text-sm text-gray-500">No notifications</p>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border-t border-gray-100 bg-gray-50">
                    <button 
                      onClick={() => {
                        setActiveTab('activity');
                        setShowNotifications(false);
                      }}
                      className="w-full py-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                    >
                      View all activity
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium cursor-pointer">
              A
            </div>
          </div>
        </header>
        <div className="p-8">
          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'analytics' && renderAnalytics()}
          {activeTab === 'products' && renderProducts()}
          {activeTab === 'users' && renderUsers()}
          {activeTab === 'alerts' && renderAlerts()}
          {activeTab === 'emails' && renderEmails()}
          {activeTab === 'activity' && renderActivity()}
          {activeTab === 'app-settings' && renderAppSettings()}
          {activeTab === 'settings' && renderSettings()}
        </div>
      </main>

      {/* Notification Detail Modal */}
      {showNotificationDetail && selectedNotification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full overflow-hidden shadow-2xl">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedNotification.color}`}>
                  <i className={selectedNotification.icon}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedNotification.title}</h3>
                  <p className="text-xs text-gray-500">{formatNotificationTime(selectedNotification.time)}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNotificationDetail(false);
                  setSelectedNotification(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
            
            <div className="p-5">
              {selectedNotification.type === 'activity' && selectedNotification.details && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold">
                      {selectedNotification.details.userName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedNotification.details.userName}</p>
                      <p className="text-sm text-gray-500">{selectedNotification.details.userEmail}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Action</span>
                      <span className="text-sm font-medium text-gray-900">{selectedNotification.details.actionLabel}</span>
                    </div>
                    
                    {selectedNotification.details.productName && (
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Product</span>
                        <span className="text-sm font-medium text-teal-600">{selectedNotification.details.productName}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">IP Address</span>
                      <span className="text-sm font-mono text-gray-900">{selectedNotification.details.ipAddress}</span>
                    </div>
                    
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Browser</span>
                      <span className="text-sm text-gray-900">{selectedNotification.details.userAgent}</span>
                    </div>
                    
                    {selectedNotification.details.extraDetails && Object.keys(selectedNotification.details.extraDetails).length > 0 && (
                      <div className="pt-2">
                        <p className="text-sm text-gray-500 mb-2">Additional Details</p>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          {Object.entries(selectedNotification.details.extraDetails).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                              <span className="text-xs font-medium text-gray-900">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {selectedNotification.type === 'alert' && selectedNotification.details && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-xl">
                    <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={selectedNotification.details.productImage} 
                        alt={selectedNotification.details.productName}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedNotification.details.productName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          {selectedNotification.details.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <p className="text-xs text-gray-500 mb-1">Original Price</p>
                      <p className="text-lg font-semibold text-gray-400 line-through">${selectedNotification.details.originalPrice}</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <p className="text-xs text-gray-500 mb-1">Dropped Price</p>
                      <p className="text-lg font-semibold text-green-600">${selectedNotification.details.droppedPrice}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl text-white text-center">
                    <p className="text-sm opacity-90">Total Savings</p>
                    <p className="text-2xl font-bold">${selectedNotification.details.savings}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Target Price</span>
                      <span className="text-sm font-medium text-gray-900">${selectedNotification.details.targetPrice}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Alert Created</span>
                      <span className="text-sm text-gray-900">{new Date(selectedNotification.details.createdAt).toLocaleDateString()}</span>
                    </div>
                    {selectedNotification.details.triggeredAt && (
                      <div className="flex items-center justify-between py-2 border-b border-gray-100">
                        <span className="text-sm text-gray-500">Triggered At</span>
                        <span className="text-sm text-gray-900">{new Date(selectedNotification.details.triggeredAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowNotificationDetail(false);
                  setSelectedNotification(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
              {selectedNotification.type === 'activity' && (
                <button
                  onClick={() => {
                    setShowNotificationDetail(false);
                    setSelectedNotification(null);
                    setActiveTab('activity');
                    setShowNotifications(false);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                >
                  View in Activity Logs
                </button>
              )}
              {selectedNotification.type === 'alert' && (
                <button
                  onClick={() => {
                    setShowNotificationDetail(false);
                    setSelectedNotification(null);
                    setActiveTab('alerts');
                    setShowNotifications(false);
                  }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                >
                  View in Alerts
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
